import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, 
  Eye, 
  Edit, 
  Trash2, 
  Filter, 
  BarChart3, 
  User, 
  Calendar,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { featureRequestsApi } from '../../services/api';

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  reviewing: { label: 'En cours de révision', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approuvée', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejetée', color: 'bg-red-100 text-red-800' },
  implemented: { label: 'Implémentée', color: 'bg-purple-100 text-purple-800' }
};

const PRIORITY_LABELS = {
  low: { label: 'Faible', color: 'glass text-gray-800' },
  medium: { label: 'Moyenne', color: 'bg-orange-100 text-orange-800' },
  high: { label: 'Élevée', color: 'bg-red-100 text-red-800' }
};

function FeatureRequestsAdmin() {
  const [featureRequests, setFeatureRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Load feature requests and stats
  const loadData = async () => {
    try {
      setLoading(true);
      const [requestsData, statsData] = await Promise.all([
        featureRequestsApi.getAll(filters),
        featureRequestsApi.getStats()
      ]);
      
      setFeatureRequests(requestsData);
      setStats(statsData);
      setError('');
    } catch (err) {
      console.error('Error loading feature requests:', err);
      setError(err.message || 'Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setIsUpdating(true);
      await featureRequestsApi.update(id, { status: newStatus });
      await loadData(); // Reload data
      
      // Update selected request if it's currently open
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePriorityChange = async (id, newPriority) => {
    try {
      setIsUpdating(true);
      await featureRequestsApi.update(id, { priority: newPriority });
      await loadData(); // Reload data
      
      // Update selected request if it's currently open
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest({ ...selectedRequest, priority: newPriority });
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      setIsUpdating(true);
      await featureRequestsApi.delete(id);
      await loadData(); // Reload data
      
      // Close detail view if deleted request was selected
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="glass p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Lightbulb className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Demandes de fonctionnalités</h2>
              <p className="text-gray-600">Gérer les suggestions des utilisateurs</p>
            </div>
          </div>
          
          {/* Stats cards */}
          <div className="flex space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.total || 0}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
              <div className="text-sm text-gray-600">En attente</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.approved || 0}</div>
              <div className="text-sm text-gray-600">Approuvées</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.implemented || 0}</div>
              <div className="text-sm text-gray-600">Implémentées</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 bg-black/30 border border-white/15 rounded-md text-white focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="px-3 py-2 bg-black/30 border border-white/15 rounded-md text-white focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="">Toutes les priorités</option>
            {Object.entries(PRIORITY_LABELS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature requests list */}
        <div className="glass rounded-lg">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Demandes ({featureRequests.length})</h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {featureRequests.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Aucune demande trouvée
              </div>
            ) : (
              <div className="divide-y">
                {featureRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`p-4 cursor-pointer hover:bg-white/[0.06] transition-colors ${
                      selectedRequest?.id === request.id ? 'bg-yellow-500/10' : ''
                    }`}
                    onClick={() => setSelectedRequest(request)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 truncate pr-2">
                        {request.title}
                      </h4>
                      <div className="flex space-x-1">
                        <span className={`px-2 py-1 text-xs rounded-full ${STATUS_LABELS[request.status]?.color}`}>
                          {STATUS_LABELS[request.status]?.label}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${PRIORITY_LABELS[request.priority]?.color}`}>
                          {PRIORITY_LABELS[request.priority]?.label}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <User className="h-3 w-3" />
                          <span>{request.user_name}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(request.created_at)}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Feature request details */}
        <div className="glass rounded-lg">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Détails</h3>
          </div>
          
          {selectedRequest ? (
            <div className="p-6 space-y-6">
              {/* Title and actions */}
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {selectedRequest.title}
                  </h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>{selectedRequest.user_name} ({selectedRequest.user_type})</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(selectedRequest.created_at)}</span>
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={() => handleDelete(selectedRequest.id)}
                  disabled={isUpdating}
                  className="text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Status and Priority controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={selectedRequest.status}
                    onChange={(e) => handleStatusChange(selectedRequest.id, e.target.value)}
                    disabled={isUpdating}
                    className="w-full px-3 py-2 bg-black/30 border border-white/15 rounded-md text-white focus:ring-yellow-500 focus:border-yellow-500 disabled:bg-white/[0.04]"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priorité
                  </label>
                  <select
                    value={selectedRequest.priority}
                    onChange={(e) => handlePriorityChange(selectedRequest.id, e.target.value)}
                    disabled={isUpdating}
                    className="w-full px-3 py-2 bg-black/30 border border-white/15 rounded-md text-white focus:ring-yellow-500 focus:border-yellow-500 disabled:bg-white/[0.04]"
                  >
                    {Object.entries(PRIORITY_LABELS).map(([value, { label }]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <div className="glass border rounded-md p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedRequest.description}
                </div>
              </div>

              {/* Admin notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes administrateur
                </label>
                <textarea
                  value={selectedRequest.admin_notes || ''}
                  onChange={(e) => {
                    setSelectedRequest({ ...selectedRequest, admin_notes: e.target.value });
                  }}
                  onBlur={() => {
                    featureRequestsApi.update(selectedRequest.id, { 
                      admin_notes: selectedRequest.admin_notes 
                    });
                  }}
                  placeholder="Ajouter des notes internes..."
                  className="w-full px-3 py-2 bg-black/30 border border-white/15 rounded-md text-white focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                />
              </div>

              {/* Contact info */}
              <div className="bg-yellow-500/100/10 border border-yellow-500/30 rounded-md p-4">
                <h5 className="font-medium text-yellow-900 mb-2">Contact</h5>
                <p className="text-sm text-yellow-700">
                  Email: {selectedRequest.user_email}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              Sélectionnez une demande pour voir les détails
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FeatureRequestsAdmin;